import { createContext } from 'react';
import * as React from 'react';
import type { PendoInstance } from '../../init/pendo/types';

interface AnalyticsContextType {
  /**
   * The Pendo instance. Will be undefined if Pendo's API Key is not set.
   */
  pendo?: PendoInstance;
  /**
   * The visitor ID. Pendo will generate a unique ID for the visitor if one is not provided, otherwise this should match userId.
   */
  visitorId: string | null;
  /**
   * The visitor email.
   *
   * @optional
   * @type string | null
   */
  visitorEmail?: string | null;
  /**
   * The account ID.
   *
   * @optional
   * @type string | null
   */
  accountId?: string | null;
  /**
   * The account ID.
   *
   * @optional
   * @type string | null
   */
  accountName?: string | null;
  /**
   * The function to call when the user has been identified via userId and optionally organizationId, organizationName, and userEmail.
   */
  onIdentify: (
    userId: string,
    organizationId?: string | null,
    organizationName?: string | null,
    userEmail?: string | null,
  ) => void;
  /**
   * The function to call when the user's session needs to be cleared, like on sign out.
   */
  onClearSession: () => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | null>(null);

const useAnalytics = () => {
  const context = React.useContext(AnalyticsContext);

  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }

  return context;
};

export { AnalyticsContext, useAnalytics };
export type { AnalyticsContextType };
